/**  @jsx jsx */

import { ComponentProps, ReactNode } from 'react';

import { Tabs, Text } from '@zapier/design-system';
import { jsx, css } from '@emotion/core';

import {
  useCurrentVersionReadOnlyReason,
  useRouter,
} from 'app/developer-v3/hooks';
import NonEditableUI from 'app/developer-v3/components/NonEditableUI';

import { isGUIIntegrationOnlyRoute } from 'app/developer-v3/utils/routeKeyChecks';

import LinkTabs from 'app/developer-v3/components/LinkTabs';

import { LayoutStyles } from './constants';

const Styles = {
  header: ({ hasPadding }) => [
    css`
      flex: 0;
    `,
    hasPadding &&
      css`
        padding: ${LayoutStyles.verticalPadding}
          ${LayoutStyles.horizontalPadding} 0 ${LayoutStyles.horizontalPadding};
      `,
  ],
  root: css`
    height: 100%;
    display: flex;
    flex: 1;
    flex-flow: column nowrap;
  `,
};

type Props = {
  children: ReactNode;
  currentUrl?: string;
  hasHeaderPadding?: boolean;
  isDisabled?: boolean;
  navConfig?: ComponentProps<typeof Tabs>['items'];
  title: string;
};

const EditorLayout = ({
  children,
  currentUrl,
  hasHeaderPadding = true,
  navConfig,
  title,
}: Props) => {
  const { routeKey } = useRouter();

  // The following hook expects a router object with a 'current' property,
  // so we can just pass in a stubbed object with the expected properties to maintain
  // compatibility with other code using the hook.
  const stubRouter = { current: { routeKey } };
  const isGUIOnlyRoute = isGUIIntegrationOnlyRoute(stubRouter);

  const currentVersionReadOnlyReason = useCurrentVersionReadOnlyReason();
  const isReadOnly = !!currentVersionReadOnlyReason;

  return (
    <div css={Styles.root}>
      <header css={Styles.header({ hasPadding: hasHeaderPadding })}>
        {title && (
          <Text
            fontWeight={600}
            margin="0px 0px 20px"
            tag="h1"
            type="PageHeader"
          >
            {title}
          </Text>
        )}
        {isReadOnly && isGUIOnlyRoute && <NonEditableUI />}
        {navConfig && <LinkTabs currentUrl={currentUrl} items={navConfig} />}
      </header>
      {children}
    </div>
  );
};

export default EditorLayout;
